/**
 * Account Quick Info – enterprise sidebar panel for tenant context
 */
import { cn } from '@respectly/ui';
import { Users, Phone, Calendar, ExternalLink, Building2 } from 'lucide-react';
import { Link } from '@tanstack/react-router';
import { AccountStatusBadge } from '../accounts/AccountStatusBadge';
import type { TenantStatus } from '@respectly/shared-types';

interface TenantInfo {
  id: string;
  name: string;
  slug: string;
  status?: TenantStatus;
  plan?: { id: string; name: string };
  userCount?: number;
  contactCount?: number;
  createdAt?: Date | string;
}

interface AccountQuickInfoProps {
  tenant: TenantInfo;
  className?: string;
}

const PLAN_COLORS: Record<string, string> = {
  starter:    'bg-zinc-100 dark:bg-zinc-800 text-zinc-600 dark:text-zinc-400',
  growth:     'bg-blue-50 dark:bg-blue-950/40 text-blue-600 dark:text-blue-400',
  pro:        'bg-purple-50 dark:bg-purple-950/40 text-purple-600 dark:text-purple-400',
  enterprise: 'bg-amber-50 dark:bg-amber-950/40 text-amber-700 dark:text-amber-400',
};

export function AccountQuickInfo({ tenant, className }: AccountQuickInfoProps) {
  const planKey = tenant.plan?.name?.toLowerCase() ?? '';
  const planColorClass = PLAN_COLORS[planKey] ?? PLAN_COLORS.starter;

  return (
    <div className={cn('border-b border-zinc-200 dark:border-zinc-800', className)}>
      {/* Tenant header */}
      <div className="px-4 pt-4 pb-3">
        <div className="flex items-start gap-3">
          <div className="shrink-0 h-10 w-10 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white font-bold text-sm shadow-md shadow-blue-500/20">
            {tenant.name.charAt(0).toUpperCase()}
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold text-zinc-900 dark:text-zinc-50 truncate leading-tight">
              {tenant.name}
            </p>
            <p className="text-xs text-zinc-400 dark:text-zinc-500 mt-0.5 font-mono truncate">
              @{tenant.slug}
            </p>
          </div>
        </div>

        {/* Status + Plan */}
        <div className="flex items-center gap-2 mt-3">
          {tenant.status && <AccountStatusBadge status={tenant.status} size="sm" />}
          {tenant.plan && (
            <span className={cn('text-[11px] font-semibold px-2 py-0.5 rounded-full ring-1 ring-inset ring-current/20', planColorClass)}>
              {tenant.plan.name}
            </span>
          )}
        </div>
      </div>

      {/* Stats grid */}
      <div className="grid grid-cols-2 border-t border-zinc-100 dark:border-zinc-800 divide-x divide-zinc-100 dark:divide-zinc-800">
        <div className="px-4 py-3">
          <p className="text-[11px] text-zinc-400 dark:text-zinc-500 uppercase tracking-wider mb-1">Users</p>
          <div className="flex items-center gap-1.5">
            <Users className="h-3.5 w-3.5 text-zinc-400" />
            <span className="text-sm font-semibold text-zinc-800 dark:text-zinc-100 font-mono">
              {tenant.userCount ?? '—'}
            </span>
          </div>
        </div>
        <div className="px-4 py-3">
          <p className="text-[11px] text-zinc-400 dark:text-zinc-500 uppercase tracking-wider mb-1">Contacts</p>
          <div className="flex items-center gap-1.5">
            <Phone className="h-3.5 w-3.5 text-zinc-400" />
            <span className="text-sm font-semibold text-zinc-800 dark:text-zinc-100 font-mono">
              {tenant.contactCount ?? '—'}
            </span>
          </div>
        </div>
      </div>

      {/* Customer since + action */}
      <div className="px-4 py-3 border-t border-zinc-100 dark:border-zinc-800 flex items-center justify-between">
        {tenant.createdAt && (
          <div className="flex items-center gap-1.5 text-xs text-zinc-500 dark:text-zinc-400">
            <Calendar className="h-3.5 w-3.5 shrink-0" />
            <span>
              Since {new Date(tenant.createdAt).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}
            </span>
          </div>
        )}
        <Link
          to="/accounts/$id"
          params={{ id: tenant.id }}
          className="inline-flex items-center gap-1.5 text-xs font-medium text-blue-500 hover:text-blue-600 dark:hover:text-blue-300 transition-colors"
        >
          <Building2 className="h-3 w-3" />
          View Account
          <ExternalLink className="h-3 w-3 opacity-60" />
        </Link>
      </div>
    </div>
  );
}

export function AccountQuickInfoSkeleton() {
  return (
    <div className="border-b border-zinc-200 dark:border-zinc-800 animate-pulse">
      <div className="px-4 pt-4 pb-3">
        <div className="flex items-start gap-3 mb-3">
          <div className="h-10 w-10 rounded-xl bg-zinc-200 dark:bg-zinc-700 shrink-0" />
          <div className="flex-1 space-y-1.5">
            <div className="h-4 bg-zinc-200 dark:bg-zinc-700 rounded w-3/4" />
            <div className="h-3 bg-zinc-100 dark:bg-zinc-800 rounded w-1/2" />
          </div>
        </div>
        <div className="flex gap-2">
          <div className="h-5 w-14 bg-zinc-200 dark:bg-zinc-700 rounded-full" />
          <div className="h-5 w-16 bg-zinc-200 dark:bg-zinc-700 rounded-full" />
        </div>
      </div>
      <div className="grid grid-cols-2 border-t border-zinc-100 dark:border-zinc-800">
        <div className="px-4 py-3 border-r border-zinc-100 dark:border-zinc-800">
          <div className="h-2.5 bg-zinc-100 dark:bg-zinc-800 rounded w-10 mb-2" />
          <div className="h-4 bg-zinc-200 dark:bg-zinc-700 rounded w-8" />
        </div>
        <div className="px-4 py-3">
          <div className="h-2.5 bg-zinc-100 dark:bg-zinc-800 rounded w-14 mb-2" />
          <div className="h-4 bg-zinc-200 dark:bg-zinc-700 rounded w-10" />
        </div>
      </div>
      <div className="px-4 py-3 border-t border-zinc-100 dark:border-zinc-800">
        <div className="h-3 bg-zinc-100 dark:bg-zinc-800 rounded w-28" />
      </div>
    </div>
  );
}
